/**
 * Defines the common functionalities of accumulation series
 */
import { isNullOrUndefined, Animation, createElement } from '@syncfusion/ej2-base';
import { degreeToLocation, getElement, indexFinder, linear } from '../../common/utils/helper';
import { pointByIndex } from '../model/acc-base';
/**
 * The `AccumulationBase` class is used to perform base calculations for accumulation charts.
 *
 * @private
 */
export class AccumulationBase {
    /** @private */
    constructor(accumulation) {
        this.accumulation = accumulation;
    }
    /**
     * Gets the center of the pie.
     *
     * @private
     * @returns {ChartLocation} - The center of the pie.
     */
    get center() {
        return this.pieCenter || (this.accumulation.visibleSeries[0].type === 'Pie' ?
            this.accumulation.pieSeriesModule.pieBaseCenter : null);
    }
    /**
     * Sets the center of the pie.
     *
     * @private
     * @param {ChartLocation} value - The center point to set.
     */
    set center(value) {
        this.pieCenter = value;
    }
    /**
     * Gets the radius of the pie.
     *
     * @private
     * @returns {number} - The radius of the pie.
     */
    get radius() {
        return this.pieRadius !== undefined ? this.pieRadius :
            this.accumulation.pieSeriesModule.pieBaseRadius;
    }
    /**
     * Sets the radius of the pie.
     *
     * @private
     * @param {number} value - The radius value to set.
     */
    set radius(value) {
        this.pieRadius = value;
    }
    /**
     * Gets the label radius of the pie.
     *
     * @private
     * @returns {number} - The label radius of the pie.
     */
    get labelRadius() {
        return this.pieLabelRadius !== undefined ? this.pieLabelRadius :
            this.accumulation.pieSeriesModule.pieBaseLabelRadius;
    }
    /**
     * Sets the label radius of the pie.
     *
     * @private
     * @param {number} value - The label radius value to set.
     */
    set labelRadius(value) {
        this.pieLabelRadius = value;
    }
    /**
     * Checks whether the series is circular or not.
     *
     * @private
     * @returns {boolean} - True if the series is circular, otherwise false.
     */
    isCircular() {
        return this.accumulation.type === 'Pie';
    }
    /**
     * To check various radius pie.
     *
     * @private
     * @returns {boolean} - True if various radius is enabled, otherwise false.
     */
    isVariousRadius() {
        return this.accumulation.pieSeriesModule.isRadiusMapped;
    }
    /**
     * To process the explode on accumulation chart loading.
     *
     * @private
     * @param {Event} event - The event triggered during loading.
     * @returns {void}
     */
    processExplode(event) {
        const indexes = indexFinder(event.target.id, true);
        const seriesIndex = indexes.series;
        const pointIndex = indexes.point;
        if (isNaN(pointIndex)) {
            return null;
        }
        const series = this.accumulation.visibleSeries[seriesIndex];
        if (!series) {
            return null;
        }
        // keep your datalabel-outside early return logic as-is, but use seriesIndex
        if ((event.target.id.indexOf('_datalabel_') > -1) &&
            series.points[pointIndex] &&
            series.points[pointIndex].labelPosition === 'Outside') {
            return null;
        }
        this.explodePoints(pointIndex, this.accumulation, undefined, undefined, seriesIndex);
        this.deExplodeAll(pointIndex, this.accumulation.enableAnimation ? 300 : 0, seriesIndex);
    }
    /**
     * To invoke the explode on accumulation chart loading.
     *
     * @param {number} seriesIndex - The series index for multi-series support.
     * @private
     * @returns {void}
     */
    invokeExplode(seriesIndex = 0) {
        const series = this.accumulation.visibleSeries[seriesIndex];
        const duration = this.accumulation.enableAnimation ? 300 : 0;
        for (const point of series.points) {
            if (point.isExplode && point.y !== 0) {
                this.pointExplode(point.index, point, duration, undefined, undefined, seriesIndex);
            }
        }
        if (this.accumulation.accumulationSelectionModule && this.accumulation.selectionMode !== 'None' &&
            this.accumulation.accumulationSelectionModule.selectedDataIndexes.length) {
            for (const index of this.accumulation.accumulationSelectionModule.selectedDataIndexes) {
                this.explodePoints(index.point, this.accumulation, true, undefined, series.index);
                this.deExplodeAll(index.point, duration);
            }
        }
    }
    /**
     * To deExplode all points in the series.
     * In multi-series mode, de-explodes all matching points across series.
     *
     * @private
     * @param {number} index - The index of the point to explode.
     * @param {number} animationDuration - The duration of the animation.
     * @param {number} seriesIndex - The series index.
     * @returns {void}
     */
    deExplodeAll(index, animationDuration, seriesIndex = 0) {
        const pointId = this.accumulation.element.id + '_Series_' + seriesIndex + '_Point_';
        const points = this.accumulation.visibleSeries[seriesIndex].points;
        for (let i = 0, pointsArray = points; i < pointsArray.length; i++) {
            const currentPoint = points[i];
            if ((index !== currentPoint.index && !currentPoint.isSliced) || currentPoint.isClubbed) {
                currentPoint.isExplode = false;
                this.deExplodeSlice(currentPoint.index, pointId, animationDuration);
            }
        }
    }
    /**
     * To explode point by index.
     * In multi-series mode, explodes all matching points across series with same mappingKey.
     *
     * @private
     * @param {number} index - The index of the point to explode.
     * @param {AccumulationChart} chart - The accumulation chart control.
     * @param {boolean} explode - Specifies whether to explode the point (default: false).
     * @param {boolean} pointAnimation - Specifies whether the point based animation is enabled.
     * @param {number} seriesIndex - Index of the series.
     * @returns {void}
     */
    explodePoints(index, chart, explode = false, pointAnimation, seriesIndex = 0) {
        const series = chart.visibleSeries[seriesIndex];
        const points = series.points;
        const point = pointByIndex(index, points);
        let explodePoints = true;
        const duration = this.accumulation.enableAnimation ? 300 : 0;
        if (isNullOrUndefined(point)) {
            return null;
        }
        const clubPointsExploded = !explode &&
            (point.isSliced || (series.clubbedPoints.length &&
                points[points.length - 1].index === series.clubbedPoints[series.clubbedPoints.length - 1].index));
        if (series.type === 'Pie' && (clubPointsExploded || point.isClubbed)) {
            explodePoints = this.clubPointExplode(index, point, series, points, chart, duration, clubPointsExploded);
        }
        if (explodePoints && point.y !== 0) {
            this.pointExplode(index, point, duration, explode, pointAnimation, series.index);
        }
    }
    getSum(points) {
        let total = 0;
        points.map((point) => {
            total += point.visible ? point.y : 0;
        });
        return total;
    }
    clubPointExplode(index, point, series, points, chart, duration, clubPointsExploded = false) {
        if (point.isClubbed) {
            chart.animateSeries = false;
            points.splice(points.length - 1, 1);
            series.clubbedPoints.map((point) => {
                point.visible = true;
                point.isExplode = true;
            });
            chart.visibleSeries[series.index].points = points.concat(series.clubbedPoints);
            this.deExplodeAll(index, duration);
            series.sumOfPoints = this.getSum(chart.visibleSeries[series.index].points);
            chart.refreshChart();
            return false;
        }
        else if (clubPointsExploded || point.isSliced) {
            chart.animateSeries = false;
            points.splice(points.length - series.clubbedPoints.length, series.clubbedPoints.length);
            const clubPoint = series.generateClubPoint();
            clubPoint.index = points.length;
            clubPoint.color = series.clubbedPoints[0].color;
            points.push(clubPoint);
            series.sumOfPoints = this.getSum(points);
            this.deExplodeAll(index, duration);
            clubPoint.isExplode = false;
            chart.visibleSeries[series.index].points = points;
            chart.refreshChart();
            this.pointExplode(clubPoint.index, points[clubPoint.index], 0, true, null, series.index);
            clubPoint.isExplode = false;
            this.deExplodeSlice(clubPoint.index, chart.element.id + '_Series_' + series.index + '_Point_', duration);
            if (point.isSliced) {
                return false;
            }
        }
        return true;
    }
    /**
     * To Explode points.
     *
     * @param {number} index - Index of a point.
     * @param {AccPoints} point - To get the point of explode.
     * @param {number} duration - Duration of the explode point.
     * @param {boolean} explode - Either true or false.
     * @param {boolean} pointAnimation - Specifies whether the point based animation is enabled.
     * @param {number} seriesIndex - Index of the series.
     * @returns {void}
     */
    pointExplode(index, point, duration, explode, pointAnimation, seriesIndex) {
        if (seriesIndex === void 0) {
            seriesIndex = 0;
        }
        let translate;
        const pointId = this.accumulation.element.id + '_Series_' + seriesIndex + '_Point_';
        const chart = this.accumulation;
        const series = chart.visibleSeries[seriesIndex];
        const explodeDistance = series.type === 'Pie' ? (series && series._explodeDistance
            ? series._explodeDistance : 0)
            : chart.explodeDistance;
        if (!this.isCircular()) {
            translate = {
                x: ((point.labelRegion && point.labelRegion.x < point.region.x) ? -explodeDistance :
                    explodeDistance), y: 0
            };
        }
        else {
            translate = degreeToLocation(point.midAngle, explodeDistance, this.center);
        }
        if (this.isExplode(pointId + index) || explode) {
            point.isExplode = true;
            this.explodeSlice(index, translate, pointId, this.center || { x: 0, y: 0 }, duration, pointAnimation);
        }
        else {
            point.isExplode = false;
            this.deExplodeSlice(index, pointId, duration);
        }
    }
    /**
     * To check point is exploded by id.
     *
     * @param {string} id - The id of the point to check.
     * @returns {boolean} - True if the point is exploded, otherwise false.
     */
    isExplode(id) {
        const element = getElement(id);
        const transform = element ? element.getAttribute('transform') : null;
        return (element && (transform === 'translate(0, 0)' || transform === null || transform === 'translate(0)'));
    }
    /**
     * To deExplode the point by index.
     *
     * @param {number} index - The index of the point.
     * @param {string} sliceId - The id of the slice.
     * @param {number} animationDuration - The duration of the animation.
     * @returns {void}
     */
    deExplodeSlice(index, sliceId, animationDuration) {
        const element = getElement(sliceId + index);
        const transform = element ? element.getAttribute('transform') : null;
        if (this.accumulation.enableAnimation && element && transform &&
            transform !== 'translate(0, 0)' && transform !== 'translate(0)') {
            const result = transform.split('(')[1].split(')')[0].split(',');
            this.performAnimation(index, sliceId, 0, 0, +result[0], +result[1] || 0, animationDuration, true);
        }
        else {
            this.performAnimation(index, sliceId, 0, 0, 0, 0, animationDuration, true);
        }
    }
    /**
     * To translate the point elements by index and position.
     *
     * @param {number} index - The index of the point.
     * @param {string} sliceId - The id of the slice.
     * @param {string} position - The position to translate the point to.
     * @param {string} transform - The transformation to apply to the point.
     * @returns {void}
     */
    setTranslate(index, sliceId, position, transform) {
        this.setElementTransform(sliceId + index, position);
        this.setElementTransform(sliceId + index + '_polygon', position);
        // Extract seriesIndex from sliceId for multi-series support
        const values = sliceId.split('_');
        const seriesIndex = parseInt(values[values.length - 3], 10);
        if (this.accumulation.visibleSeries[seriesIndex] &&
            this.accumulation.visibleSeries[seriesIndex].dataLabel.visible) {
            sliceId = this.accumulation.element.id + '_datalabel_Series_' + seriesIndex + '_';
            this.setElementTransform(sliceId + 'shape_' + index, position);
            this.setElementTransform(sliceId + 'text_' + index, position + transform);
            this.setElementTransform(sliceId + 'connector_' + index, position);
        }
    }
    /**
     * To translate the point element by id and position.
     *
     * @param {string} id - The id of the point element.
     * @param {string} position - The position to translate the point to.
     * @returns {void}
     */
    setElementTransform(id, position) {
        const element = getElement(id);
        if (element) {
            element.setAttribute('transform', position);
        }
    }
    /**
     * To translate the point elements by index position.
     *
     * @param {number} index - The index of the point.
     * @param {ChartLocation} translate - The translation values (x, y).
     * @param {string} sliceId - The id of the slice.
     * @param {ChartLocation} center - The center point of the accumulation chart.
     * @param {number} animationDuration - The duration of the animation.
     * @param {boolean} pointAnimation - Specifies whether the point based animation is enabled.
     * @returns {void}
     */
    explodeSlice(index, translate, sliceId, center, animationDuration, pointAnimation) {
        const values = sliceId.split('_');
        const seriesIndex = parseInt(sliceId.split('_')[values.length - 3], 10);
        if (pointAnimation
            && getElement(this.accumulation.element.id + '_Series_' + seriesIndex + '_Point_' + index).getAttribute('transform')) {
            this.setElementTransform(sliceId + index, `translate(${translate.x - center.x}, ${translate.y - center.y})`);
        }
        else {
            this.performAnimation(index, sliceId, 0, 0, translate.x - center.x, translate.y - center.y, animationDuration);
        }
    }
    /**
     * To Perform animation point explode.
     *
     * @param {number} index - Index of the series.
     * @param {string} sliceId - ID of the series.
     * @param {number} startX - X value of start.
     * @param {number} startY - Y value of start.
     * @param {number} endX - X value of end.
     * @param {number} endY - Y value of end.
     * @param {number} duration - Duration of the animation.
     * @param {boolean} isReverse - Duration of the animation.
     * @returns {void}
     */
    performAnimation(index, sliceId, startX, startY, endX, endY, duration, isReverse) {
        const chart = this.accumulation;
        const values = sliceId.split('_');
        const seriesIndex = parseInt(sliceId.split('_')[values.length - 3], 10);
        const point = chart.visibleSeries[seriesIndex].points[index];
        if (duration <= 0) {
            this.setTranslate(index, sliceId, 'translate(' + (endX) + ', ' + (endY) + ')', point.transform);
            return null;
        }
        let xValue;
        let yValue;
        const delay = (this.accumulation.series[seriesIndex].type === 'Pie')
            && (this.accumulation.animateSeries) && (this.accumulation.series[0].animation.enable)
            ? this.accumulation.series[0].animation.duration : 0;
        new Animation({}).animate(createElement('div'), {
            duration: duration,
            delay: delay,
            progress: (args) => {
                xValue = linear(args.timeStamp, startX, endX, args.duration);
                yValue = linear(args.timeStamp, startY, endY, args.duration);
                this.setTranslate(index, sliceId, 'translate(' + (isReverse ? endX - xValue : xValue) + ', ' + (isReverse ? endY - yValue : yValue) + ')', point.transform);
            },
            end: () => {
                this.setTranslate(index, sliceId, 'translate(' + (isReverse ? startX : endX) + ', ' + (isReverse ? startX : endY) + ')', point.transform);
            }
        });
    }
}
